import React, {
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { getCurrentFocusKey } from '@noriginmedia/norigin-spatial-navigation'
import {
  NetworkMonitorContextValue,
  NetworkMonitorProviderProps,
} from './types'
import { useConnectivityStatus } from './useConnectivityStatus'

const NetworkMonitorContext = createContext<
  NetworkMonitorContextValue | undefined
>(undefined)

function NetworkMonitorProvider({ children }: NetworkMonitorProviderProps) {
  const { isConnected } = useConnectivityStatus()
  const previouslyConnectedRef = useRef(isConnected)
  const lastFocusKeyRef = useRef<string | null>(null)
  const [connectionRecovered, setConnectionRecovered] = useState(false)
  const [suppressAutoErrorModal, setSuppressAutoErrorModal] = useState(false)

  // Handle connection state changes - useLayoutEffect for synchronous execution
  useLayoutEffect(() => {
    const wasConnected = previouslyConnectedRef.current
    const isNowConnected = isConnected

    previouslyConnectedRef.current = isNowConnected

    if (!wasConnected && isNowConnected) {
      // Reconnected - hide modal and set message for video playback
      lastFocusKeyRef.current = null
      setConnectionRecovered(true)
      // Clear the flag after 10 seconds
      const timeout = setTimeout(() => setConnectionRecovered(false), 10000)
      return () => clearTimeout(timeout)
    }

    if (wasConnected && !isNowConnected) {
      // Disconnected - capture focus BEFORE showing modal (synchronous)
      lastFocusKeyRef.current = getCurrentFocusKey()
      setConnectionRecovered(false)
    }

    return undefined
  }, [isConnected])

  const contextValue = useMemo(
    () => ({
      isDisconnected: !isConnected,
      isOnline: isConnected,
      lastFocusKey: lastFocusKeyRef.current,
      connectionRecovered,
      suppressAutoErrorModal,
      setSuppressAutoErrorModal,
    }),
    [isConnected, connectionRecovered, suppressAutoErrorModal]
  )

  return (
    <NetworkMonitorContext.Provider value={contextValue}>
      {children}
    </NetworkMonitorContext.Provider>
  )
}

export const useNetworkMonitor = (): NetworkMonitorContextValue => {
  const context = useContext(NetworkMonitorContext)
  if (!context) {
    throw new Error(
      'useNetworkMonitor must be used within a NetworkMonitorProvider'
    )
  }
  return context
}

export default NetworkMonitorProvider
